import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional, SkipSelf } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { environment } from '../../../environments/environment';
import { ProviderLinkService } from '../../core/services';
import { ApiService } from '../../domain/services/api.service';
import {
    AuthenticationResponse,
    CreateUserModel,
    JwtToken,
    User
} from '../models';
import { LocalAccountCreationService } from './local-account-creation.service';

type SessionKey = 'accessToken' | 'user';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _user = new BehaviorSubject<User | null>(null);
    private _accountCreated = new ReplaySubject<void>();
    private _signedIn = new ReplaySubject<void>();
    private _tokenExpiration = new Subject<void>();
    private _previousIsSignedIn: boolean | undefined;

    constructor(
        private readonly _http: HttpClient,
        private readonly _apiService: ApiService,
        private readonly _router: Router,
        private readonly _localAccountCreation: LocalAccountCreationService,
        private readonly _providerLink: ProviderLinkService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        @Optional() @SkipSelf() parent?: AuthService
    ) {
        // Make sure that the service is a singleton in the entire application
        if (parent) {
            throw Error(
                `[AuthService]: trying to create multiple instances, but this service should be a singleton`
            );
        }

        this.user$.pipe(filter(Boolean)).subscribe((_) => {
            if (this.cachedClientCode !== runtimeConfig.clientCode) {
                this.signOut();
            }
        });

        if (this.isSignedIn) {
            if (this.cachedClientCode !== runtimeConfig.clientCode) {
                this.signOut();
            }

            // I want to get the user from the database right away if I'm signed in.
            // But because transloco is using the HttpClient to get translation files
            // from assets, doing this http call right away messes up with transloco
            // initialization somehow.  I couldn't figure out the root cause, but
            // doing this after things calm down seemed to fix the issue.  Would
            // like to know why this is the case.
            // - JDS 5/22/2021
            setTimeout(() =>
                this.getUser(this.getSessionItem('user').id).subscribe(
                    (user) => {
                        this._user.next(user);
                    }
                )
            );
        }
    }

    get user$() {
        return this._user.asObservable();
    }

    get accountCreated$(): Observable<void> {
        return this._accountCreated.asObservable();
    }

    get successfulSignIn$(): Observable<void> {
        return this._signedIn.asObservable();
    }

    get isSignedIn(): boolean {
        const isTokenExpired = this.isTokenExpired();
        const currentIsSignedIn =
            !!this.accessToken &&
            !!this.getSessionItem('user') &&
            !isTokenExpired;

        if (this._previousIsSignedIn !== currentIsSignedIn) {
            if (
                this._previousIsSignedIn === true &&
                currentIsSignedIn === false &&
                isTokenExpired
            ) {
                this._tokenExpiration.next();
            }
            this._previousIsSignedIn = currentIsSignedIn;
        }

        return currentIsSignedIn;
    }

    get accessToken(): string {
        return this.getSessionItem('accessToken');
    }

    get tokenExpiration$(): Observable<void> {
        return this._tokenExpiration.asObservable();
    }

    private get cachedClientCode(): string {
        sessionStorage.setItem('currentClientCode', runtimeConfig.clientCode);
        return runtimeConfig.clientCode;
    }

    /**
     * Requests a security code from the API.  Use when signing in.
     */
    requestSignInCode(emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendSignInUserCode`, {
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Requests a security code from the API. Use when creating the users account.
     */
    requestCreateAccountCode(emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendCreateAccountCode`, {
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Requests a security code from the API.  Use when updating a user's email or phone number.
     */
    requestUpdateUserCode(userId: number, emailOrPhoneNumber: string) {
        return this._apiService.post(`/Users/SendUpdateUserCode`, {
            userId,
            clientId: runtimeConfig.clientId,
            emailOrPhoneNumber: emailOrPhoneNumber
        });
    }

    /**
     * Sign in with email or phone number and security code received via SMS or email previously.
     */
    signIn(emailOrPhoneNumber: string, securityCode: number) {
        const request = this._http
            .post<AuthenticationResponse>(
                `${environment.apiUrl}/Users/Signin`,
                {
                    clientId: runtimeConfig.clientId,
                    emailOrPhoneNumber: emailOrPhoneNumber,
                    securityCode: securityCode
                }
            )
            .pipe(
                tap((response: AuthenticationResponse) =>
                    this.storeTokenAndUser(response)
                ),
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => {
                    this._user.next(user);
                    this._signedIn.next();
                    this.setCurrentSecurityCode(securityCode);

                    this._googleAnalyticsService.login();

                    // redirect everyone that just signed up to the application
                    this._router.navigate([
                        `${runtimeConfig.clientCode}/application`
                    ]);
                })
            );

        return request;
    }

    /**
     * Create account, and if successful, sign-in
     */
    signUp(createUser: CreateUserModel) {
        const request = this._http
            .post<AuthenticationResponse>(
                `${environment.apiUrl}/Users`,
                createUser
            )
            .pipe(
                tap((response: AuthenticationResponse) =>
                    this.storeTokenAndUser(response)
                ),
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => {
                    this._user.next(user);
                    this._signedIn.next();
                    this.setCurrentSecurityCode(createUser.securityCode);

                    this._googleAnalyticsService.signUp();

                    // Look for special indicator of waitlist
                    var waitlistIds = sessionStorage.getItem(
                        'waitlistProviderIds'
                    );
                    if (waitlistIds !== null) {
                        sessionStorage.setItem(
                            'applicationProviderIds',
                            waitlistIds
                        );
                        sessionStorage.removeItem('waitlistProviderIds');
                    }

                    // redirect everyone that just signed up to the application
                    if (
                        waitlistIds === null &&
                        sessionStorage.getItem('applicationProviderIds') ===
                            null
                    )
                        this._router.navigate([
                            `${runtimeConfig.clientCode}/provider`
                        ]);
                    else
                        this._router.navigate([
                            `${runtimeConfig.clientCode}/application`
                        ]);
                })
            );

        return request;
    }

    signOut() {
        this.clearSession();
        this._user.next(null);
        this._localAccountCreation.clear();
        if (this._providerLink.hasProvider)
            this._router.navigate([
                `${runtimeConfig.clientCode}/providers/${this._providerLink.providerId}`
            ]);
        else this._router.navigate([`${runtimeConfig.clientCode}/home`]);
    }

    getUser(id: number) {
        return this._http.get<User>(`${environment.apiUrl}/Users/${id}`);
    }

    updateUser(user: User) {
        const request$ = this._http
            .put<AuthenticationResponse>(
                `${environment.apiUrl}/Users/${user.userId}`,
                {
                    ...user,
                    securityCode: this.getCurrentSecurityCode()
                }
            )
            .pipe(
                switchMap((response) => this.getUser(response.userId)),
                tap((user) => this._user.next(user))
            );

        return request$;
    }

    setCurrentSecurityCode(code: number) {
        sessionStorage.setItem('code', JSON.stringify(code));
    }

    private getCurrentSecurityCode(): number | null {
        const code = sessionStorage.getItem('code');
        return code ? (JSON.parse(code) as number) : null;
    }

    /**
     * Sets accessToken and parses the token to store the email/phone number and user's id.
     */
    private storeTokenAndUser(response: AuthenticationResponse) {
        this.clearSession();

        this.setSessionItem('accessToken', response.accessToken);
        const token = this.getParsedJwt(response.accessToken);
        this.setSessionItem('user', {
            emailOrPhone:
                token?.[
                    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
                ],
            id: response.userId
        });
    }

    private clearSession() {
        // remove authentication related data from session
        this.removeSessionItem('accessToken');
        this.removeSessionItem('user');
    }

    private setSessionItem(name: SessionKey, item: any) {
        sessionStorage.setItem(name, JSON.stringify(item));
    }

    private getSessionItem(name: SessionKey) {
        const item = sessionStorage.getItem(name);
        if (item) return JSON.parse(item);
        else return null;
    }

    private removeSessionItem(name: SessionKey) {
        sessionStorage.removeItem(name);
    }

    /**
     * Returns a JS object representation of a Javascript Web Token from its common encoded string form.
     *
     * Borrowed from https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library#answer-65248576
     * @param token a Javascript Web Token in base64 encoded, '.' separated form
     * @returns an object-representation of the token
     */
    private getParsedJwt(token: string): JwtToken | null {
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (error) {
            // TODO: LOG ME
            return null;
        }
    }

    private isTokenExpired(): boolean {
        const token = this.accessToken;
        if (token) {
            const parsed = this.getParsedJwt(token);
            if (parsed) {
                return Date.now() >= parsed.exp * 1000;
            }
        }
        return false;
    }
}
